import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/shadcn-ui/dialog';
import { Button } from '@/components/shadcn-ui/button';
import { Loader2, CreditCard, Coins } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

function PermitPaymentModal({ isOpen, onClose, permitId, userId, permitChatId }) {
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [creditsLeft, setCreditsLeft] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [creditUseLoading, setCreditUseLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setCreditsLeft(null);
    setCreditsLoading(true);
    appAxios
      .get('/Permit/v1.0/check-credits')
      .then(res => {
        console.log(res.data);
        if (res.data?.status == "Success") setCreditsLeft(res.data.data);
      })
      .catch(() => { })
      .finally(() => setCreditsLoading(false));
  }, [isOpen]);

  const handlePay = async () => {
    setPayLoading(true);
    try {
      const res = await appAxios.post('/Payment/v1.0/create-base-fees-payment-link', {
        permitId,
        userId,
        permitChatId,
      });
      if (res.data?.status === 'Success') {
        window.location.href = res.data.data.url;
      } else {
        toast.error('Error creating payment link. Please try again.');
      }
    } catch {
      toast.error('Error creating payment link. Please try again.');
    } finally {
      setPayLoading(false);
    }
  };

  const handleUseCredit = async () => {
    setCreditUseLoading(true);
    try {
      const res = await appAxios.post('/Permit/v1.0/update-credits', { permitId: permitId });
      if (res.data?.status === 'Success' || res.data?.success) {
        toast.success('Credit applied! Your permit application is being processed.');
        const confirmedPermitId = res.data?.data?._id || permitId;
        localStorage.setItem(`permitChat_${permitChatId}_permitId`, confirmedPermitId);
        onClose();
        if (res.data?.data?._id) {
          window.location.href = `/citypermit/project/chat/${permitChatId}/permit/${res.data.data._id}`;
        } else {
          window.location.href = '/citypermit/project';
        }
      } else {
        toast.error('Failed to apply credit. Please try again.');
      }
    } catch {
      toast.error('Failed to apply credit. Please try again.');
    } finally {
      setCreditUseLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md dark text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">Process Your Permit Application</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Pay the base Permit AI fee to proceed with your application.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3">
          <button
            onClick={handlePay}
            disabled={payLoading || creditUseLoading}
            className="w-full rounded-lg border border-border bg-[#1e1e1e] hover:bg-[#282828] p-4 text-left transition-colors disabled:opacity-50"
          >
            <div className="flex items-center gap-3">
              <CreditCard className="h-5 w-5 text-[#3b82f6] shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Pay with Card</p>
                <p className="text-xs text-muted-foreground mt-0.5">One-time payment to process your application</p>
              </div>
              {payLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <span className="text-base font-semibold text-[#3b82f6]">$49.00</span>
              )}
            </div>
          </button>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <span>or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <div className="rounded-lg border border-border bg-[#1e1e1e] p-4">
            <div className="flex items-center gap-3">
              <Coins className="h-5 w-5 text-amber-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Use a Credit</p>
                <p className="text-xs text-muted-foreground mt-0.5">For subscribed users</p>
              </div>
              {creditsLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              {!creditsLoading && creditsLeft !== null && (
                <span className="text-xs text-muted-foreground">{creditsLeft} left</span>
              )}
            </div>

            {!creditsLoading && creditsLeft !== null && creditsLeft > 0 && (
              <Button
                onClick={handleUseCredit}
                disabled={payLoading || creditUseLoading}
                className="mt-3 w-full bg-blue-500 hover:bg-blue-600 text-white"
                size="sm"
              >
                {creditUseLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Use 1 Credit to Process'}
              </Button>
            )}

            {!creditsLoading && (creditsLeft === null || creditsLeft === 0) && (
              <p className="mt-3 text-xs text-muted-foreground text-center">
                {creditsLeft === 0 ? 'No credits remaining.' : 'No active subscription found.'}
              </p>
            )}
          </div>

          <Button variant="outline" onClick={onClose} className="w-full" disabled={payLoading || creditUseLoading}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default PermitPaymentModal;
